import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_BASE_URL } from '../config/api';

const Timeline = () => {
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editor view states
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null); // timeline form data
  const [activeModuleIndex, setActiveModuleIndex] = useState(null);
  const [activeWeekIndex, setActiveWeekIndex] = useState({}); // moduleIndex -> weekIndex

  const apiBase = API_BASE_URL;

  useEffect(() => {
    fetchTimelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTimelines = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/api/timelines`);
      const data = await res.json();
      if (res.ok && data.success) {
        setTimelines(data.data || []);
      } else {
        toast.error(data.message || 'Failed to fetch timelines');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error fetching timelines');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!window.confirm('Are you sure you want to seed default roadmap timelines? This will insert standard roadmaps.')) return;
    try {
      setSeeding(true);
      const res = await fetch(`${apiBase}/api/timelines/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Roadmap data seeded successfully!');
        fetchTimelines();
      } else {
        toast.error(data.message || 'Failed to seed data');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error seeding data');
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this roadmap timeline? This cannot be undone.')) return;
    try {
      const res = await fetch(`${apiBase}/api/timelines/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Timeline deleted successfully');
        fetchTimelines();
      } else {
        toast.error(data.message || 'Failed to delete timeline');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error deleting timeline');
    }
  };

  // Convert timeline data from DB format (arrays of strings) to form editing format (newline separated strings)
  const convertApiToForm = (timeline) => {
    if (!timeline) return null;
    return {
      ...timeline,
      title: timeline.title || '',
      slug: timeline.slug || '',
      totalDuration: timeline.totalDuration || '',
      modules: (timeline.modules || []).map(mod => ({
        ...mod,
        moduleTitle: mod.moduleTitle || '',
        weekRange: mod.weekRange || '',
        description: mod.description || '',
        weeks: (mod.weeks || []).map(wk => ({
          ...wk,
          weekTitle: wk.weekTitle || '',
          timePerDay: wk.timePerDay || '',
          goals: (wk.goals || []).join('\n'),
          focus: (wk.focus || []).join('\n'),
          resources: (wk.resources || []).join('\n'),
          outputs: (wk.outputs || []).join('\n'),
          exampleCapstoneIdeas: (wk.exampleCapstoneIdeas || []).join('\n'),
          activities: (wk.activities || []).map(act => ({
            ...act,
            category: act.category || '',
            items: (act.items || []).join('\n')
          })),
          tracks: (wk.tracks || []).map(tr => ({
            ...tr,
            name: tr.name || '',
            description: tr.description || '',
            items: (tr.items || []).join('\n')
          })),
          assessmentQuestions: (wk.assessmentQuestions || []).map(q => ({
            ...q,
            question: q.question || '',
            details: q.details || ''
          }))
        }))
      }))
    };
  };

  // Convert form data (newline separated strings) back to API format (arrays of strings)
  const convertFormToApi = (formData) => {
    const splitLines = (str) => {
      if (!str) return [];
      if (Array.isArray(str)) return str;
      return str.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    };

    return {
      ...formData,
      modules: (formData.modules || []).map(mod => ({
        ...mod,
        weeks: (mod.weeks || []).map(wk => ({
          ...wk,
          goals: splitLines(wk.goals),
          focus: splitLines(wk.focus),
          resources: splitLines(wk.resources),
          outputs: splitLines(wk.outputs),
          exampleCapstoneIdeas: splitLines(wk.exampleCapstoneIdeas),
          activities: (wk.activities || []).map(act => ({
            ...act,
            items: splitLines(act.items)
          })),
          tracks: (wk.tracks || []).map(tr => ({
            ...tr,
            items: splitLines(tr.items)
          }))
        }))
      }))
    };
  };

  const handleStartCreate = () => {
    const newTimeline = {
      title: 'New Roadmap Timeline',
      slug: 'new-roadmap-timeline',
      totalDuration: '24 weeks',
      modules: []
    };
    setFormData(convertApiToForm(newTimeline));
    setIsEditing(true);
    setActiveModuleIndex(null);
  };

  const handleStartEdit = (timeline) => {
    setFormData(convertApiToForm(timeline));
    setIsEditing(true);
    setActiveModuleIndex(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'title' && !prev._id) {
        // Auto-generate slug for new roadmaps
        updated.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)+/g, '');
      }
      return updated;
    });
  };

  // ─── Module Operations ──────────────────────────────────────────────────────
  const addModule = () => {
    setFormData(prev => {
      const modules = prev.modules || [];
      const nextNumber = modules.reduce((max, m) => Math.max(max, m.moduleNumber), -1) + 1;
      return {
        ...prev,
        modules: [
          ...modules,
          {
            moduleNumber: nextNumber,
            moduleTitle: 'New Module',
            weekRange: 'Weeks X-Y',
            description: '',
            weeks: []
          }
        ]
      };
    });
    setTimeout(() => {
      setActiveModuleIndex((formData?.modules || []).length);
    }, 50);
  };

  const deleteModule = (mIdx) => {
    if (!window.confirm('Delete this module and all its weeks?')) return;
    setFormData(prev => {
      const updated = prev.modules.filter((_, i) => i !== mIdx);
      // Re-assign module numbers for sequencing
      const sequenced = updated.map((m, idx) => ({ ...m, moduleNumber: idx }));
      return { ...prev, modules: sequenced };
    });
    if (activeModuleIndex === mIdx) setActiveModuleIndex(null);
  };

  const handleModuleChange = (mIdx, field, value) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      modules[mIdx] = { ...modules[mIdx], [field]: value };
      return { ...prev, modules };
    });
  };

  // ─── Week Operations ────────────────────────────────────────────────────────
  const addWeek = (mIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = modules[mIdx].weeks || [];
      const nextWeekNum = weeks.reduce((max, w) => Math.max(max, w.weekNumber), -1) + 1;
      
      const newWeek = {
        weekNumber: nextWeekNum,
        weekTitle: 'New Week Title',
        timePerDay: '~3 h/day',
        goals: '',
        focus: '',
        resources: '',
        outputs: '',
        exampleCapstoneIdeas: '',
        activities: [],
        tracks: [],
        assessmentQuestions: []
      };

      modules[mIdx] = {
        ...modules[mIdx],
        weeks: [...weeks, newWeek]
      };
      return { ...prev, modules };
    });

    setActiveWeekIndex(prev => ({
      ...prev,
      [mIdx]: (formData.modules[mIdx].weeks || []).length
    }));
  };

  const deleteWeek = (mIdx, wIdx) => {
    if (!window.confirm('Are you sure you want to delete this week?')) return;
    setFormData(prev => {
      const modules = [...prev.modules];
      const updatedWeeks = modules[mIdx].weeks.filter((_, i) => i !== wIdx);
      // Re-assign week numbers to maintain sequential numbers
      const sequencedWeeks = updatedWeeks.map((w, idx) => ({ ...w, weekNumber: idx }));
      modules[mIdx] = { ...modules[mIdx], weeks: sequencedWeeks };
      return { ...prev, modules };
    });
  };

  const handleWeekChange = (mIdx, wIdx, field, value) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      weeks[wIdx] = { ...weeks[wIdx], [field]: value };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  // ─── Nested Activities, Tracks, Questions ──────────────────────────────────
  const addActivity = (mIdx, wIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const activities = weeks[wIdx].activities || [];
      weeks[wIdx] = {
        ...weeks[wIdx],
        activities: [...activities, { category: 'New Category', items: '' }]
      };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const handleActivityChange = (mIdx, wIdx, actIdx, field, value) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const activities = [...weeks[wIdx].activities];
      activities[actIdx] = { ...activities[actIdx], [field]: value };
      weeks[wIdx] = { ...weeks[wIdx], activities };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const deleteActivity = (mIdx, wIdx, actIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      weeks[wIdx] = {
        ...weeks[wIdx],
        activities: weeks[wIdx].activities.filter((_, i) => i !== actIdx)
      };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const addQuestion = (mIdx, wIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const questions = weeks[wIdx].assessmentQuestions || [];
      weeks[wIdx] = {
        ...weeks[wIdx],
        assessmentQuestions: [
          ...questions,
          { questionNumber: questions.length + 1, question: '', details: '' }
        ]
      };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const handleQuestionChange = (mIdx, wIdx, qIdx, field, value) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const questions = [...weeks[wIdx].assessmentQuestions];
      questions[qIdx] = { ...questions[qIdx], [field]: value };
      weeks[wIdx] = { ...weeks[wIdx], assessmentQuestions: questions };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const deleteQuestion = (mIdx, wIdx, qIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const updatedQuestions = weeks[wIdx].assessmentQuestions.filter((_, i) => i !== qIdx);
      // Resequence question numbers
      const sequenced = updatedQuestions.map((q, idx) => ({ ...q, questionNumber: idx + 1 }));
      weeks[wIdx] = { ...weeks[wIdx], assessmentQuestions: sequenced };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const addTrack = (mIdx, wIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const tracks = weeks[wIdx].tracks || [];
      weeks[wIdx] = {
        ...weeks[wIdx],
        tracks: [...tracks, { name: 'New Track', description: '', items: '' }]
      };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const handleTrackChange = (mIdx, wIdx, tIdx, field, value) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      const tracks = [...weeks[wIdx].tracks];
      tracks[tIdx] = { ...tracks[tIdx], [field]: value };
      weeks[wIdx] = { ...weeks[wIdx], tracks };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  const deleteTrack = (mIdx, wIdx, tIdx) => {
    setFormData(prev => {
      const modules = [...prev.modules];
      const weeks = [...modules[mIdx].weeks];
      weeks[wIdx] = {
        ...weeks[wIdx],
        tracks: weeks[wIdx].tracks.filter((_, i) => i !== tIdx)
      };
      modules[mIdx] = { ...modules[mIdx], weeks };
      return { ...prev, modules };
    });
  };

  // ─── Submit Form ────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) {
      toast.error('Title and Slug are required.');
      return;
    }

    try {
      setSaving(true);
      const apiPayload = convertFormToApi(formData);
      const isNew = !formData._id;
      const url = isNew ? `${apiBase}/api/timelines` : `${apiBase}/api/timelines/${formData._id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      });
      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(isNew ? 'Roadmap timeline created!' : 'Roadmap timeline updated!');
        setIsEditing(false);
        setFormData(null);
        fetchTimelines();
      } else {
        toast.error(data.message || 'Error saving timeline');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error while saving');
    } finally {
      setSaving(false);
    }
  };

  const filteredTimelines = timelines.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-10">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6 mb-6 border-b border-gray-200">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Curriculum Roadmaps (Timelines)</h1>
          <p className="mt-1 text-sm text-gray-500">Create, edit, and sequence timelines, modules, and weeks.</p>
        </div>
        {!isEditing && (
          <div className="mt-4 sm:mt-0 flex gap-3">
            <button
              onClick={handleSeedData}
              disabled={seeding}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {seeding ? 'Seeding...' : '🌱 Seed Defaults'}
            </button>
            <button
              onClick={handleStartCreate}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md text-white bg-blue-600 shadow-sm hover:bg-blue-700 transition-colors"
            >
              ➕ Create Roadmap
            </button>
          </div>
        )}
      </div>

      {/* Main interface */}
      {!isEditing ? (
        // TIMELINE LIST VIEW
        <div>
          <div className="mb-6">
            <input
              type="text"
              placeholder="🔍 Search roadmaps by title or slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-md px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            />
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="mt-4 text-gray-500 font-medium">Loading roadmaps...</span>
            </div>
          ) : filteredTimelines.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
              <p className="text-lg text-gray-500 font-medium">No timelines found.</p>
              <p className="text-sm text-gray-400 mt-1">Try searching for something else or seed the default data.</p>
              <button
                onClick={handleSeedData}
                disabled={seeding}
                className="mt-6 inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors"
              >
                🌱 Seed Default Roadmaps
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTimelines.map((timeline) => {
                const totalWeeks = (timeline.modules || []).reduce((acc, m) => acc + (m.weeks || []).length, 0);
                return (
                  <div key={timeline._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-100">
                          {timeline.totalDuration || 'N/A'}
                        </span>
                        <span className="text-xs text-gray-400">ID: {timeline._id.slice(-6)}</span>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 line-clamp-1">{timeline.title}</h2>
                      <p className="text-xs font-mono text-gray-500 mt-1 mb-4 bg-gray-50 p-1.5 rounded">{timeline.slug}</p>
                      
                      <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 text-sm text-gray-600">
                        <div>
                          <p className="text-xs text-gray-400 font-semibold uppercase">Modules</p>
                          <p className="text-lg font-bold text-gray-800">{(timeline.modules || []).length}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 font-semibold uppercase">Total Weeks</p>
                          <p className="text-lg font-bold text-gray-800">{totalWeeks}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-150 flex justify-between items-center">
                      <button
                        onClick={() => handleStartEdit(timeline)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-800"
                      >
                        ✏️ Edit Roadmap
                      </button>
                      <button
                        onClick={() => handleDelete(timeline._id)}
                        className="text-sm font-semibold text-red-600 hover:text-red-800"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        // TIMELINE EDITOR VIEW
        <form onSubmit={handleSave} className="space-y-6">
          {/* Editor Header: Sticky */}
          <div className="sticky top-0 z-30 bg-white py-4 px-6 -mx-4 sm:-mx-6 lg:-mx-8 border-b border-gray-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Discard unsaved changes?')) {
                    setIsEditing(false);
                    setFormData(null);
                  }
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-150 transition-colors"
              >
                ⬅️ Back
              </button>
              <div>
                <span className="text-xs uppercase text-gray-400 font-bold tracking-wider">Roadmap Editor</span>
                <h2 className="text-xl font-bold text-gray-800 leading-tight">
                  {formData._id ? `Edit: ${formData.title}` : 'Create New Roadmap'}
                </h2>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(null);
                }}
                className="px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : '💾 Save Roadmap'}
              </button>
            </div>
          </div>

          {/* Form Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">General Roadmap Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Roadmap Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g. Mobile Robotics Engineer Roadmap"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Roadmap Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g. mobile-robotics-engineer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Total Duration</label>
                <input
                  type="text"
                  name="totalDuration"
                  value={formData.totalDuration}
                  onChange={handleFormChange}
                  placeholder="e.g. 24 weeks"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
            </div>
          </div>

          {/* Modules Accordion Wrapper */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-xl font-bold text-gray-900">Roadmap Modules</h3>
              <button
                type="button"
                onClick={addModule}
                className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
              >
                ➕ Add Module
              </button>
            </div>

            {formData.modules.length === 0 ? (
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-10 text-center text-gray-500">
                This roadmap has no modules yet. Click "Add Module" to start structuring your curriculum.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.modules.map((module, mIdx) => {
                  const isExpanded = activeModuleIndex === mIdx;
                  return (
                    <div key={mIdx} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      {/* Module Header */}
                      <div
                        onClick={() => setActiveModuleIndex(isExpanded ? null : mIdx)}
                        className={`flex items-center justify-between px-6 py-4 cursor-pointer select-none transition-colors hover:bg-gray-50 ${isExpanded ? 'bg-gray-50 border-b border-gray-200' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex items-center justify-center bg-gray-200 text-gray-800 font-bold h-7 w-7 rounded-full text-sm">
                            {module.moduleNumber}
                          </span>
                          <div>
                            <h4 className="font-bold text-gray-800 text-base">{module.moduleTitle || 'Untitled Module'}</h4>
                            <span className="text-xs text-gray-500 font-semibold">{module.weekRange || 'No week range'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => deleteModule(mIdx)}
                            className="text-xs font-semibold text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                          >
                            🗑️ Delete Module
                          </button>
                          <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>
                        </div>
                      </div>

                      {/* Module Fields */}
                      {isExpanded && (
                        <div className="p-6 space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Module Title</label>
                              <input
                                type="text"
                                value={module.moduleTitle}
                                onChange={(e) => handleModuleChange(mIdx, 'moduleTitle', e.target.value)}
                                placeholder="Module Title"
                                className="w-full px-3 py-1.5 border border-gray-300 rounded bg-white text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Week Range</label>
                              <input
                                type="text"
                                value={module.weekRange}
                                onChange={(e) => handleModuleChange(mIdx, 'weekRange', e.target.value)}
                                placeholder="e.g. Weeks 1-4"
                                className="w-full px-3 py-1.5 border border-gray-300 rounded bg-white text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Module Number</label>
                              <input
                                type="number"
                                value={module.moduleNumber}
                                onChange={(e) => handleModuleChange(mIdx, 'moduleNumber', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded bg-white text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Description</label>
                            <textarea
                              value={module.description}
                              onChange={(e) => handleModuleChange(mIdx, 'description', e.target.value)}
                              rows={2}
                              placeholder="Describe this module..."
                              className="w-full px-3 py-1.5 border border-gray-300 rounded bg-white text-sm"
                            />
                          </div>

                          {/* Weeks Accordion inside Module */}
                          <div className="border-t border-gray-200 pt-4 space-y-4">
                            <div className="flex items-center justify-between">
                              <h5 className="font-bold text-gray-800 text-sm">Weeks inside this Module ({module.weeks.length})</h5>
                              <button
                                type="button"
                                onClick={() => addWeek(mIdx)}
                                className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-colors"
                              >
                                ➕ Add Week
                              </button>
                            </div>

                            {module.weeks.length === 0 ? (
                              <div className="bg-gray-50 rounded border border-dashed p-6 text-center text-xs text-gray-400">
                                No weeks added to this module yet.
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {module.weeks.map((week, wIdx) => {
                                  const weekExpanded = activeWeekIndex[mIdx] === wIdx;
                                  return (
                                    <div key={wIdx} className="border border-gray-250 rounded bg-gray-50/50 overflow-hidden">
                                      {/* Week Header */}
                                      <div
                                        onClick={() => {
                                          setActiveWeekIndex(prev => ({
                                            ...prev,
                                            [mIdx]: weekExpanded ? null : wIdx
                                          }));
                                        }}
                                        className={`flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-gray-100 select-none ${weekExpanded ? 'bg-gray-100 border-b border-gray-250' : ''}`}
                                      >
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className="font-bold text-gray-500">W{week.weekNumber}:</span>
                                          <span className="font-semibold text-gray-700">{week.weekTitle || 'Untitled Week'}</span>
                                          <span className="text-xs text-gray-400">({week.timePerDay || 'N/A'})</span>
                                        </div>
                                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                          <button
                                            type="button"
                                            onClick={() => deleteWeek(mIdx, wIdx)}
                                            className="text-xs text-red-500 hover:text-red-700 p-0.5 hover:bg-red-50 rounded"
                                          >
                                            Delete Week
                                          </button>
                                          <span className="text-gray-400 text-xs">{weekExpanded ? '▼' : '▶'}</span>
                                        </div>
                                      </div>

                                      {/* Week details form */}
                                      {weekExpanded && (
                                        <div className="p-4 bg-white space-y-4 text-xs">
                                          {/* Week basics */}
                                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Week Number</label>
                                              <input
                                                type="number"
                                                value={week.weekNumber}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'weekNumber', parseInt(e.target.value) || 0)}
                                                className="w-full px-2 py-1 border border-gray-300 rounded"
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Week Title</label>
                                              <input
                                                type="text"
                                                value={week.weekTitle}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'weekTitle', e.target.value)}
                                                className="w-full px-2 py-1 border border-gray-300 rounded"
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Time Commitment</label>
                                              <input
                                                type="text"
                                                value={week.timePerDay}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'timePerDay', e.target.value)}
                                                placeholder="e.g. ~4 h/day"
                                                className="w-full px-2 py-1 border border-gray-300 rounded"
                                              />
                                            </div>
                                          </div>

                                          {/* Newline-separated lists */}
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3">
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Goals (one per line)</label>
                                              <textarea
                                                value={week.goals}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'goals', e.target.value)}
                                                rows={3}
                                                className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                placeholder="Enter goals, one per line..."
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Focus Areas (one per line)</label>
                                              <textarea
                                                value={week.focus}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'focus', e.target.value)}
                                                rows={3}
                                                className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                placeholder="Enter focus points, one per line..."
                                              />
                                            </div>
                                          </div>

                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Resources Links/Names (one per line)</label>
                                              <textarea
                                                value={week.resources}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'resources', e.target.value)}
                                                rows={3}
                                                className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                placeholder="Enter resource links..."
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Week Outputs/Deliverables (one per line)</label>
                                              <textarea
                                                value={week.outputs}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'outputs', e.target.value)}
                                                rows={3}
                                                className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                placeholder="Enter deliverables..."
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-bold text-gray-600 mb-1">Capstone Ideas (one per line)</label>
                                              <textarea
                                                value={week.exampleCapstoneIdeas}
                                                onChange={(e) => handleWeekChange(mIdx, wIdx, 'exampleCapstoneIdeas', e.target.value)}
                                                rows={3}
                                                className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                placeholder="Enter capstone project ideas..."
                                              />
                                            </div>
                                          </div>

                                          {/* Nested Array Objects: Activities */}
                                          <div className="border-t pt-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                              <span className="font-bold text-gray-700">Activities Categories & Tasks</span>
                                              <button
                                                type="button"
                                                onClick={() => addActivity(mIdx, wIdx)}
                                                className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                                              >
                                                + Add Category
                                              </button>
                                            </div>
                                            {(!week.activities || week.activities.length === 0) ? (
                                              <p className="text-gray-400 italic">No activity categories added yet.</p>
                                            ) : (
                                              <div className="space-y-3">
                                                {week.activities.map((act, actIdx) => (
                                                  <div key={actIdx} className="bg-gray-50 p-2.5 rounded border border-gray-200 relative">
                                                    <button
                                                      type="button"
                                                      onClick={() => deleteActivity(mIdx, wIdx, actIdx)}
                                                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold"
                                                    >
                                                      ×
                                                    </button>
                                                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                                      <div className="sm:col-span-1">
                                                        <label className="block font-bold text-gray-500 mb-0.5">Category Name</label>
                                                        <input
                                                          type="text"
                                                          value={act.category}
                                                          onChange={(e) => handleActivityChange(mIdx, wIdx, actIdx, 'category', e.target.value)}
                                                          placeholder="e.g. ROS 2"
                                                          className="w-full px-2 py-1 border border-gray-300 rounded"
                                                        />
                                                      </div>
                                                      <div className="sm:col-span-3">
                                                        <label className="block font-bold text-gray-500 mb-0.5">Tasks/Items (one per line)</label>
                                                        <textarea
                                                          value={act.items}
                                                          onChange={(e) => handleActivityChange(mIdx, wIdx, actIdx, 'items', e.target.value)}
                                                          rows={2}
                                                          className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                          placeholder="Task 1&#10;Task 2"
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>

                                          {/* Nested Array Objects: Tracks */}
                                          <div className="border-t pt-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                              <span className="font-bold text-gray-700">Specialization Tracks</span>
                                              <button
                                                type="button"
                                                onClick={() => addTrack(mIdx, wIdx)}
                                                className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                                              >
                                                + Add Track
                                              </button>
                                            </div>
                                            {(!week.tracks || week.tracks.length === 0) ? (
                                              <p className="text-gray-400 italic">No specialization tracks added.</p>
                                            ) : (
                                              <div className="space-y-3">
                                                {week.tracks.map((tr, tIdx) => (
                                                  <div key={tIdx} className="bg-gray-50 p-2.5 rounded border border-gray-200 relative">
                                                    <button
                                                      type="button"
                                                      onClick={() => deleteTrack(mIdx, wIdx, tIdx)}
                                                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold"
                                                    >
                                                      ×
                                                    </button>
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                      <div>
                                                        <label className="block font-bold text-gray-500 mb-0.5">Track Name</label>
                                                        <input
                                                          type="text"
                                                          value={tr.name}
                                                          onChange={(e) => handleTrackChange(mIdx, wIdx, tIdx, 'name', e.target.value)}
                                                          placeholder="e.g. Navigation Focus"
                                                          className="w-full px-2 py-1 border border-gray-300 rounded"
                                                        />
                                                        <label className="block font-bold text-gray-500 mt-2 mb-0.5">Track Description</label>
                                                        <textarea
                                                          value={tr.description}
                                                          onChange={(e) => handleTrackChange(mIdx, wIdx, tIdx, 'description', e.target.value)}
                                                          rows={2}
                                                          className="w-full px-2 py-1 border border-gray-300 rounded"
                                                          placeholder="Brief summary..."
                                                        />
                                                      </div>
                                                      <div className="sm:col-span-2">
                                                        <label className="block font-bold text-gray-500 mb-0.5">Track Items/Tasks (one per line)</label>
                                                        <textarea
                                                          value={tr.items}
                                                          onChange={(e) => handleTrackChange(mIdx, wIdx, tIdx, 'items', e.target.value)}
                                                          rows={4}
                                                          className="w-full px-2 py-1 border border-gray-300 rounded font-sans"
                                                          placeholder="Item 1&#10;Item 2"
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>

                                          {/* Nested Array Objects: Assessment Questions */}
                                          <div className="border-t pt-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                              <span className="font-bold text-gray-700">Assessment Questions</span>
                                              <button
                                                type="button"
                                                onClick={() => addQuestion(mIdx, wIdx)}
                                                className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                                              >
                                                + Add Question
                                              </button>
                                            </div>
                                            {(!week.assessmentQuestions || week.assessmentQuestions.length === 0) ? (
                                              <p className="text-gray-400 italic">No assessment questions added.</p>
                                            ) : (
                                              <div className="space-y-3">
                                                {week.assessmentQuestions.map((q, qIdx) => (
                                                  <div key={qIdx} className="bg-gray-50 p-2.5 rounded border border-gray-200 relative">
                                                    <button
                                                      type="button"
                                                      onClick={() => deleteQuestion(mIdx, wIdx, qIdx)}
                                                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold"
                                                    >
                                                      ×
                                                    </button>
                                                    <div className="font-semibold text-gray-600 mb-2">Question #{q.questionNumber}</div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                      <div>
                                                        <label className="block font-bold text-gray-500 mb-0.5">Question Text</label>
                                                        <textarea
                                                          value={q.question}
                                                          onChange={(e) => handleQuestionChange(mIdx, wIdx, qIdx, 'question', e.target.value)}
                                                          rows={2}
                                                          className="w-full px-2 py-1 border border-gray-300 rounded"
                                                          placeholder="Write the question..."
                                                        />
                                                      </div>
                                                      <div>
                                                        <label className="block font-bold text-gray-500 mb-0.5">Question Details / Guidance</label>
                                                        <textarea
                                                          value={q.details}
                                                          onChange={(e) => handleQuestionChange(mIdx, wIdx, qIdx, 'details', e.target.value)}
                                                          rows={2}
                                                          className="w-full px-2 py-1 border border-gray-300 rounded"
                                                          placeholder="Provide submission guidelines/hints..."
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>

                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default Timeline;
